import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import React from "react";

type ModalSizes = "sm" | "md" | "lg" | "xl" | "full";

type CommonModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void | null;
  title?: string;
  size?: ModalSizes;
  children: React.ReactNode;
};

const sizeClasses: Record<ModalSizes, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  full: "w-screen h-screen max-w-none rounded-none",
};

export default function CommonModal({
  open,
  onOpenChange,
  title,
  size = "md",
  children,
}: CommonModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>

        {/* Overlay */}
        <Dialog.Overlay
          className="
            fixed inset-0 z-50
            bg-black/50
          "
        />

        {/* Content */}
        <Dialog.Content
          className={`
            fixed z-50
            top-1/2 left-1/2
            -translate-x-1/2 -translate-y-1/2
            w-[90%]
            ${sizeClasses[size]}
            bg-white
            rounded-xl
            p-6
            shadow-xl
            focus:outline-none
          `}
          aria-describedby="modal-description"
        >
          {/* Visually hidden description for screen readers (required by Radix UI) */}
          <Dialog.Description id="modal-description" className="sr-only">
            {title ?? "Modal dialog"}
          </Dialog.Description>
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            {title ? (
              <Dialog.Title className="text-xl font-bold text-foreground">
                {title}
              </Dialog.Title>
            ) : (
              <div /> // Spacer to keep close button on the right
            )}

            <Dialog.Close asChild>
              <button
                className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-all focus:outline-none cursor-pointer"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="max-h-[75vh] overflow-y-auto scrollbar-width-none">
            {children}
          </div>
        </Dialog.Content>

      </Dialog.Portal>
    </Dialog.Root>
  );
}